// deliveryService.js — מחזור חיי משלוח מול Firestore (אוסף "deliveries").

import {
  collection,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
  runTransaction,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import { splitFare } from "./fareEngine";

export const DELIVERY_STATUS = {
  REQUESTED: "requested",
  ACCEPTED: "accepted",
  PICKED_UP: "picked_up",
  DELIVERED: "delivered",
  CANCELLED: "cancelled",
};

export const PACKAGE_SIZES = ["small", "medium", "large"];

// עוזר: מאתר טלפון בכל אחד מהשמות האפשריים, ולעולם לא מחזיר undefined
function getPhone(u) {
  return u?.phone || u?.phoneNumber || null;
}

export async function createDelivery({ sender, pickup, dropoff, parcel, estimate, paymentMethod = "cash" }) {
  const delivery = {
    senderId: sender.uid,
    senderName: sender.fullName || sender.displayName || "ללא שם",
    senderPhone: getPhone(sender),
    senderImage: sender.profileImage || null,

    pickup,
    dropoff,

    parcel: {
      description: parcel.description || "",
      size: parcel.size || "small",
      recipientName: parcel.recipientName || "",
      recipientPhone: parcel.recipientPhone || "",
      notes: parcel.notes || "",
    },

    distanceKm: estimate.distanceKm ?? null,
    durationMin: estimate.durationMin ?? null,
    fare: estimate.fare ?? null,
    finalFare: null,

    paymentMethod,
    platformCommission: null,
    courierPayout: null,

    status: DELIVERY_STATUS.REQUESTED,
    courierId: null,
    courierName: null,
    courierPhone: null,
    courierVehicle: null,
    cancelledBy: null,

    requestedAt: serverTimestamp(),
    acceptedAt: null,
    pickedUpAt: null,
    deliveredAt: null,
    cancelledAt: null,
  };

  const ref = await addDoc(collection(db, "deliveries"), delivery);
  return ref.id;
}

export async function claimDelivery(deliveryId, courier) {
  const ref = doc(db, "deliveries", deliveryId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("המשלוח לא קיים");
    const data = snap.data();
    if (data.status !== DELIVERY_STATUS.REQUESTED || data.courierId) {
      throw new Error("המשלוח כבר נתפס על ידי שליח אחר");
    }
    tx.update(ref, {
      courierId: courier.uid,
      courierName: courier.fullName || courier.displayName || "שליח",
      courierPhone: getPhone(courier),
      courierVehicle: courier.courierInfo?.vehicle || null,
      status: DELIVERY_STATUS.ACCEPTED,
      acceptedAt: serverTimestamp(),
    });
    return true;
  });
}

export async function markPickedUp(deliveryId) {
  await updateDoc(doc(db, "deliveries", deliveryId), {
    status: DELIVERY_STATUS.PICKED_UP,
    pickedUpAt: serverTimestamp(),
  });
}

export async function completeDelivery(deliveryId, finalFare) {
  const { platformCommission, courierPayout } = splitFare(finalFare);
  await updateDoc(doc(db, "deliveries", deliveryId), {
    status: DELIVERY_STATUS.DELIVERED,
    finalFare,
    platformCommission,
    courierPayout,
    deliveredAt: serverTimestamp(),
  });
}

export async function cancelDelivery(deliveryId, by) {
  await updateDoc(doc(db, "deliveries", deliveryId), {
    status: DELIVERY_STATUS.CANCELLED,
    cancelledBy: by,
    cancelledAt: serverTimestamp(),
  });
}

export function listenToOpenDeliveries(callback) {
  const q = query(collection(db, "deliveries"), where("status", "==", DELIVERY_STATUS.REQUESTED));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

export function listenToDelivery(deliveryId, callback) {
  return onSnapshot(doc(db, "deliveries", deliveryId), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
  });
}

// מאזין למשלוח הפעיל של השולח (כדי לצוף מסך מעקב אוטומטית)
export function listenToMyActiveDelivery(senderId, callback) {
  const q = query(
    collection(db, "deliveries"),
    where("senderId", "==", senderId),
    where("status", "in", [
      DELIVERY_STATUS.REQUESTED,
      DELIVERY_STATUS.ACCEPTED,
      DELIVERY_STATUS.PICKED_UP,
    ])
  );
  return onSnapshot(q, (snap) => {
    const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(docs[0] || null);
  });
}